import { useCallback } from "react";
import { useHotkeys } from "~/lib/hotkeys";
import { usePlayerActions } from "../actions";
import { usePlaybackSource } from "../playback";

const PREVIOUS_FRAME_SHORTCUT = "j";
const PLAYBACK_SHORTCUT = "k";
const NEXT_FRAME_SHORTCUT = "l";

export function usePlaybackHotkeys() {
  const { isLoading, bounds, timestamp, isPlaying, inRangeMode } =
    usePlaybackSource();

  const { pause, restart, play, previousFrame, nextFrame } = usePlayerActions();

  const isAtStart = timestamp === bounds?.startTime;
  const isAtEnd = timestamp === bounds?.endTime;

  useHotkeys(
    PLAYBACK_SHORTCUT,
    useCallback(
      (e: KeyboardEvent) => {
        if (isLoading || inRangeMode) {
          return;
        }

        if (!checkIsAcceptableTarget(e.target)) {
          return;
        }

        if (isPlaying) {
          pause();
        } else if (isAtEnd) {
          restart();
        } else {
          play();
        }
      },
      [isLoading, inRangeMode, isPlaying, isAtEnd, pause, restart, play],
    ),
  );

  useHotkeys(
    PREVIOUS_FRAME_SHORTCUT,
    useCallback(
      (e: KeyboardEvent) => {
        if (isLoading || inRangeMode || isAtStart) {
          return;
        }

        if (!checkIsAcceptableTarget(e.target)) {
          return;
        }

        previousFrame();
      },
      [isLoading, inRangeMode, isAtStart, previousFrame],
    ),
  );

  useHotkeys(
    NEXT_FRAME_SHORTCUT,
    useCallback(
      (e: KeyboardEvent) => {
        if (isLoading || inRangeMode || isAtEnd) {
          return;
        }

        if (!checkIsAcceptableTarget(e.target)) {
          return;
        }

        nextFrame();
      },
      [isLoading, inRangeMode, isAtEnd, nextFrame],
    ),
  );

  return {
    previousFrameShortcut: PREVIOUS_FRAME_SHORTCUT,
    nextFrameShortcut: NEXT_FRAME_SHORTCUT,
    playShortcut: PLAYBACK_SHORTCUT,
    pauseShortcut: PLAYBACK_SHORTCUT,
    restartShortcut: PLAYBACK_SHORTCUT,
  };
}

const ALLOWABLE_INPUT_TYPES = [
  "button",
  "checkbox",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
];

function checkIsAcceptableTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) {
    return false;
  }

  if (target instanceof HTMLInputElement) {
    return ALLOWABLE_INPUT_TYPES.includes(target.type);
  }

  return true;
}
